// Analytics mock data
export interface Metric {
  id: string;
  name: string;
  value: number;
  previousValue: number;
  change: number;
  changeType: 'increase' | 'decrease' | 'neutral';
  unit?: string;
  format: 'number' | 'currency' | 'percent' | 'duration';
}

export interface TimeSeriesData {
  timestamp: Date;
  value: number;
  label?: string;
}

export interface Report {
  id: string;
  name: string;
  description: string;
  type: 'table' | 'chart' | 'summary';
  createdBy: string;
  createdAt: Date;
  lastRunAt: Date;
  schedule?: 'daily' | 'weekly' | 'monthly';
  status: 'active' | 'paused' | 'draft';
}

export interface ExportRecord {
  id: string;
  reportId: string;
  reportName: string;
  format: 'csv' | 'xlsx' | 'pdf' | 'json';
  size: number;
  createdBy: string;
  createdAt: Date;
  status: 'completed' | 'processing' | 'failed';
  downloadUrl?: string;
}

export const mockMetrics: Metric[] = [
  {
    id: 'metric-1',
    name: 'Total Revenue',
    value: 1245678,
    previousValue: 1102345,
    change: 13.0,
    changeType: 'increase',
    format: 'currency',
  },
  {
    id: 'metric-2',
    name: 'Active Users',
    value: 45678,
    previousValue: 42345,
    change: 7.9,
    changeType: 'increase',
    format: 'number',
  },
  {
    id: 'metric-3',
    name: 'Conversion Rate',
    value: 3.45,
    previousValue: 3.21,
    change: 7.5,
    changeType: 'increase',
    unit: '%',
    format: 'percent',
  },
  {
    id: 'metric-4',
    name: 'Avg. Session Duration',
    value: 324,
    previousValue: 298,
    change: 8.7,
    changeType: 'increase',
    unit: 'seconds',
    format: 'duration',
  },
  {
    id: 'metric-5',
    name: 'Bounce Rate',
    value: 32.5,
    previousValue: 35.2,
    change: -7.7,
    changeType: 'decrease',
    unit: '%',
    format: 'percent',
  },
  {
    id: 'metric-6',
    name: 'New Signups',
    value: 1234,
    previousValue: 1156,
    change: 6.7,
    changeType: 'increase',
    format: 'number',
  },
  {
    id: 'metric-7',
    name: 'Customer Satisfaction',
    value: 4.6,
    previousValue: 4.5,
    change: 2.2,
    changeType: 'increase',
    unit: '/5',
    format: 'number',
  },
  {
    id: 'metric-8',
    name: 'Support Tickets',
    value: 156,
    previousValue: 189,
    change: -17.5,
    changeType: 'decrease',
    format: 'number',
  },
];

// Generate time series data for the last 30 days
const generateTimeSeriesData = (baseValue: number, variance: number): TimeSeriesData[] => {
  const data: TimeSeriesData[] = [];
  const now = new Date();
  for (let i = 29; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    data.push({
      timestamp: date,
      value: baseValue + Math.floor(Math.random() * variance * 2) - variance,
    });
  }
  return data;
};

export const revenueTimeSeries = generateTimeSeriesData(40000, 8000);
export const usersTimeSeries = generateTimeSeriesData(1500, 300);
export const sessionsTimeSeries = generateTimeSeriesData(5000, 1000);
export const conversionsTimeSeries = generateTimeSeriesData(150, 30);

// Traffic sources
export const trafficSources = [
  { source: 'Organic Search', visitors: 18500, percentage: 35, color: 'blue' },
  { source: 'Direct', visitors: 12750, percentage: 24, color: 'green' },
  { source: 'Social Media', visitors: 9000, percentage: 17, color: 'pink' },
  { source: 'Referral', visitors: 7250, percentage: 14, color: 'orange' },
  { source: 'Email', visitors: 4000, percentage: 8, color: 'cyan' },
  { source: 'Paid Ads', visitors: 1500, percentage: 2, color: 'red' },
];

// Top pages
export const topPages = [
  { page: '/home', views: 45678, avgTime: 125, bounceRate: 25 },
  { page: '/products', views: 32456, avgTime: 180, bounceRate: 35 },
  { page: '/pricing', views: 28934, avgTime: 95, bounceRate: 40 },
  { page: '/about', views: 15678, avgTime: 65, bounceRate: 55 },
  { page: '/contact', views: 12345, avgTime: 45, bounceRate: 30 },
  { page: '/blog', views: 9876, avgTime: 210, bounceRate: 45 },
  { page: '/docs', views: 8765, avgTime: 320, bounceRate: 20 },
  { page: '/signup', views: 7654, avgTime: 85, bounceRate: 60 },
];

// Device breakdown
export const deviceBreakdown = [
  { device: 'Desktop', sessions: 28500, percentage: 54, color: 'blue' },
  { device: 'Mobile', sessions: 19800, percentage: 38, color: 'green' },
  { device: 'Tablet', sessions: 4200, percentage: 8, color: 'orange' },
];

// Geographic data
export const geoData = [
  { country: 'United States', visitors: 25000, revenue: 450000 },
  { country: 'United Kingdom', visitors: 8500, revenue: 125000 },
  { country: 'Germany', visitors: 6200, revenue: 95000 },
  { country: 'Canada', visitors: 5800, revenue: 85000 },
  { country: 'France', visitors: 4500, revenue: 72000 },
  { country: 'Australia', visitors: 3800, revenue: 65000 },
  { country: 'Japan', visitors: 2900, revenue: 48000 },
  { country: 'Brazil', visitors: 2100, revenue: 32000 },
];

export const mockReports: Report[] = [
  {
    id: 'report-1',
    name: 'Monthly Revenue Report',
    description: 'Comprehensive revenue breakdown by product, region, and channel',
    type: 'table',
    createdBy: 'Jane Smith',
    createdAt: new Date('2023-06-15'),
    lastRunAt: new Date('2024-01-20'),
    schedule: 'monthly',
    status: 'active',
  },
  {
    id: 'report-2',
    name: 'User Engagement Dashboard',
    description: 'Daily active users, session duration, and feature usage metrics',
    type: 'chart',
    createdBy: 'John Doe',
    createdAt: new Date('2023-09-01'),
    lastRunAt: new Date('2024-01-21'),
    schedule: 'daily',
    status: 'active',
  },
  {
    id: 'report-3',
    name: 'Conversion Funnel Analysis',
    description: 'Step-by-step conversion tracking from visit to purchase',
    type: 'chart',
    createdBy: 'Diana Prince',
    createdAt: new Date('2023-11-10'),
    lastRunAt: new Date('2024-01-19'),
    schedule: 'weekly',
    status: 'active',
  },
  {
    id: 'report-4',
    name: 'Customer Acquisition Cost',
    description: 'CAC by marketing channel with ROI calculations',
    type: 'summary',
    createdBy: 'Julia Roberts',
    createdAt: new Date('2024-01-05'),
    lastRunAt: new Date('2024-01-18'),
    status: 'active',
  },
  {
    id: 'report-5',
    name: 'Product Performance',
    description: 'Sales and engagement metrics by product category',
    type: 'table',
    createdBy: 'Michael Scott',
    createdAt: new Date('2023-08-20'),
    lastRunAt: new Date('2024-01-15'),
    schedule: 'weekly',
    status: 'paused',
  },
  {
    id: 'report-6',
    name: 'Support Ticket Analysis',
    description: 'Ticket volume, resolution time, and satisfaction scores',
    type: 'chart',
    createdBy: 'Tina Turner',
    createdAt: new Date('2024-01-10'),
    lastRunAt: new Date('2024-01-21'),
    schedule: 'daily',
    status: 'active',
  },
  {
    id: 'report-7',
    name: 'Churn Risk Assessment',
    description: 'Predictive analysis of customer churn likelihood',
    type: 'summary',
    createdBy: 'Victor Stone',
    createdAt: new Date('2024-01-15'),
    lastRunAt: new Date('2024-01-15'),
    status: 'draft',
  },
];

export const mockExports: ExportRecord[] = [
  {
    id: 'export-1',
    reportId: 'report-1',
    reportName: 'Monthly Revenue Report',
    format: 'xlsx',
    size: 2456789,
    createdBy: 'Jane Smith',
    createdAt: new Date('2024-01-20T14:30:00'),
    status: 'completed',
    downloadUrl: '/exports/revenue-jan-2024.xlsx',
  },
  {
    id: 'export-2',
    reportId: 'report-2',
    reportName: 'User Engagement Dashboard',
    format: 'pdf',
    size: 1234567,
    createdBy: 'John Doe',
    createdAt: new Date('2024-01-21T09:15:00'),
    status: 'completed',
    downloadUrl: '/exports/engagement-report.pdf',
  },
  {
    id: 'export-3',
    reportId: 'report-3',
    reportName: 'Conversion Funnel Analysis',
    format: 'csv',
    size: 345678,
    createdBy: 'Diana Prince',
    createdAt: new Date('2024-01-19T16:45:00'),
    status: 'completed',
    downloadUrl: '/exports/funnel-data.csv',
  },
  {
    id: 'export-4',
    reportId: 'report-1',
    reportName: 'Monthly Revenue Report',
    format: 'pdf',
    size: 0,
    createdBy: 'Jane Smith',
    createdAt: new Date('2024-01-21T11:00:00'),
    status: 'processing',
  },
  {
    id: 'export-5',
    reportId: 'report-6',
    reportName: 'Support Ticket Analysis',
    format: 'json',
    size: 567890,
    createdBy: 'Tina Turner',
    createdAt: new Date('2024-01-18T10:30:00'),
    status: 'completed',
    downloadUrl: '/exports/tickets-data.json',
  },
  {
    id: 'export-6',
    reportId: 'report-4',
    reportName: 'Customer Acquisition Cost',
    format: 'xlsx',
    size: 0,
    createdBy: 'Julia Roberts',
    createdAt: new Date('2024-01-17T14:00:00'),
    status: 'failed',
  },
];

// Realtime stats (simulated)
export const realtimeStats = {
  activeUsers: 234,
  pageViews: 1567,
  events: 4523,
  avgLoadTime: 1.8,
  errorRate: 0.12,
  requestsPerSecond: 45,
};

// Analytics overview stats
export const analyticsStats = {
  totalVisitors: 52500,
  visitorsChange: 12.5,
  pageViews: 245678,
  pageViewsChange: 8.3,
  avgSessionDuration: 324,
  sessionDurationChange: 5.2,
  bounceRate: 32.5,
  bounceRateChange: -2.1,
};
